/**
 * CORE-204 — the feed post attachment limits, checked on the client.
 *
 * Until now nothing checked size before the upload started: the author picked a
 * file, watched it upload, and only then got a server 400 — and for video not
 * even that, because the presigned path (CORE-338) never told the server a size
 * at all. QA's note was that the error is uninformative; the deeper problem is
 * that it arrives after the bytes are already gone.
 *
 * These numbers mirror the backend, which stays the authority:
 * `CommunityFeedController.MaxImageBytes` and `PostVideoRules.MaxVideoBytes`.
 * The client check exists to fail fast and say something useful, not to be the
 * control — a caller that skips it still gets rejected server-side.
 */

export const MAX_POST_PHOTO_BYTES = 20 * 1024 * 1024;
export const MAX_POST_VIDEO_BYTES = 200 * 1024 * 1024;
/**
 * CORE-1221 — a regular user's publication goes through a different controller
 * (`POST /api/user/circle/posts`), and its cap is four times tighter than the
 * master feed's. Checking against the 20 MB number here would let the author
 * wait out an upload the server was always going to refuse.
 */
export const MAX_CIRCLE_POST_PHOTO_BYTES = 5 * 1024 * 1024;

const MB = 1024 * 1024;

/** «34 МБ» — whole megabytes; a decimal here reads as false precision. */
export function formatMb(bytes: number): string {
  return `${Math.round(bytes / MB)} МБ`;
}

/**
 * The message to show, or null when the file is fine. Says the limit AND the
 * actual size: «too large» alone leaves the author guessing how much to trim.
 *
 * An unknown size (some pickers omit it) passes — refusing to attach a file
 * because we could not measure it would be worse than letting the server judge.
 */
export function postAttachmentTooLarge(
  kind: 'photo' | 'video',
  sizeBytes: number | undefined | null,
): string | null {
  if (typeof sizeBytes !== 'number' || !Number.isFinite(sizeBytes) || sizeBytes <= 0) return null;

  const limit = kind === 'photo' ? MAX_POST_PHOTO_BYTES : MAX_POST_VIDEO_BYTES;
  if (sizeBytes <= limit) return null;

  return kind === 'photo'
    ? `Фото до ${formatMb(limit)}, а это — ${formatMb(sizeBytes)}. Выбери файл поменьше или сожми этот.`
    : `Видео до ${formatMb(limit)}, а это — ${formatMb(sizeBytes)}. Выбери файл поменьше.`;
}

/** As {@link postAttachmentTooLarge}, against the Circle post's 5 MB cap. */
export function circlePostPhotoTooLarge(sizeBytes: number | undefined | null): string | null {
  if (typeof sizeBytes !== 'number' || !Number.isFinite(sizeBytes) || sizeBytes <= 0) return null;
  if (sizeBytes <= MAX_CIRCLE_POST_PHOTO_BYTES) return null;
  return `Фото до ${formatMb(MAX_CIRCLE_POST_PHOTO_BYTES)}, а это — ${formatMb(sizeBytes)}. `
    + 'Выбери файл поменьше или сожми этот.';
}

/**
 * The image types a browser is guaranteed to render, and the only ones we store.
 *
 * CORE-1226 — GIF добавлен, и это не расширение возможностей, а исправление
 * расхождения: сервер принимал его всегда
 * (`PostPhotoRules.AllowedContentTypes` — jpeg, png, webp, gif), а клиент
 * отказывал раньше, чем спрашивал. Список здесь ДОЛЖЕН совпадать с серверным:
 * на нём же стоит `preparePostPhoto`, и всё, чего в нём нет, переводится в
 * JPEG. Для GIF это означало бы потерю анимации — то есть подмену того, что
 * человек выбрал.
 */
export const POST_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * CORE-1226 — сколько фотографий помещается в одну публикацию.
 *
 * Столько же, сколько принимает сервер (`PostPhotoRules.MaxPhotos`). Проверять
 * это на клиенте нужно не из недоверия: одиннадцатая фотография иначе уедет в
 * хранилище и будет отвергнута уже после загрузки — человек подождёт байты,
 * которые никто не собирался принимать.
 */
export const MAX_POST_PHOTOS = 10;

/**
 * CORE-338 — names a photo format the feed cannot show as-is, or null when the
 * type is fine (or unknown, which we let through and label as JPEG as before).
 *
 * `presignAndPutPostImage` converts first and only surfaces this when it cannot
 * — see the note there. Until now it quietly relabelled any unsupported type as
 * `image/jpeg`, so a HEIC from an iPhone was stored under a `.jpg` key with an
 * `image/jpeg` content type. It uploads, the post publishes, and then the tile
 * is simply blank in every browser except Safari — no error anywhere. Two such
 * photos are sitting in a production post right now (master 6, post 168): all
 * three objects return 200, and two of them are `ftypheic` bytes no browser can
 * decode.
 */
export function unsupportedPhotoFormat(mimeType: string | undefined | null): string | null {
  const type = (mimeType || '').toLowerCase().trim();
  if (!type || POST_IMAGE_TYPES.includes(type)) return null;

  if (type === 'image/heic' || type === 'image/heif') {
    return 'Это фото в формате HEIC, и открыть его здесь не получилось. Сохрани его как JPEG или переключи камеру iPhone на «Наиболее совместимый» в Настройках.';
  }
  return 'Такой формат фото мы показать не сможем. Подойдут JPEG, PNG, WebP или GIF.';
}
